import type { SearchSelectValue } from "modules/permit/components/SearchSelectField"
import type { PermitFormState, PermitPageMode, PermitRequestLayoutState } from "../types"
import { DEFAULT_TENANT_ID } from "shared/utils/tenantContext"

// IRPC-specific requirement: ENQA monitors/audits excavation-work permits and must be able to
// see and update their section's fields before the workflow reaches ENQA's own stage - scoped to
// this explicit status whitelist rather than every stage/status, per go-live sign-off. If this
// product is sold outside IRPC, this override should move to tenant-level config rather than
// staying hardcoded.
export const ALWAYS_EDITABLE_SECTION_ROLE_CODES = ["ENQA"]
export const ALWAYS_EDITABLE_STATUS_CODES = ["P0251", "P0252", "P0253", "P9009"]

export const THAI_SUBMIT_APPROVAL_LABEL = "\u0e2a\u0e48\u0e07\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34"
export const THAI_CHANGE_WORK_TIME_LABEL =
  "\u0e40\u0e1b\u0e25\u0e35\u0e48\u0e22\u0e19\u0e41\u0e1b\u0e25\u0e07\u0e40\u0e27\u0e25\u0e32\u0e01\u0e32\u0e23\u0e17\u0e33\u0e07\u0e32\u0e19"
export const THAI_EXTEND_PERMIT_LABEL = "\u0e15\u0e48\u0e2d Permit"

export const DEFAULT_PERMIT_DATA: PermitFormState = {
  permitNo: "",
  requestorName: "",
  requestorCompany: "",
  permitType: "",
  permitTypeCode: "",
  workDescription: "",
  workLocation: "",
  unit: "",
  building: "",
  floor: "",
  room: "",
  other: "",
  startDate: "",
  endDate: "",
  startTime: "",
  endTime: "",
  controller: null as SearchSelectValue | null,
  hazardousArea: "",
  plantStatus: "",
}

export const TENANT_ID = DEFAULT_TENANT_ID
export const DRAFT_STATUS = "P0010"
export const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const resolveNormalizedUserId = (user?: Record<string, any> | null): string | null => {
  if (!user) {
    return null
  }

  const candidates: Array<string | null | undefined> = [
    user.id,
    user.user_id,
    user.userId,
    user.emp_id,
    user.empId,
    user.employee_id,
    user.employeeId,
  ]

  let fallback: string | null = null

  for (const candidate of candidates) {
    if (typeof candidate !== "string") {
      continue
    }
    const trimmed = candidate.trim()
    if (!trimmed) {
      continue
    }
    const normalized = trimmed.toLowerCase()
    if (GUID_PATTERN.test(trimmed)) {
      return normalized
    }
    if (!fallback) {
      fallback = normalized
    }
  }

  return fallback
}

export const collectNormalizedUserRoleCodeCandidates = (user?: Record<string, any> | null): string[] => {
  if (!user) {
    return []
  }

  const normalized = new Set<string>()
  const add = (value: unknown) => {
    if (typeof value !== "string") {
      return
    }
    const trimmed = value.trim()
    if (!trimmed) {
      return
    }
    normalized.add(trimmed.toUpperCase())
  }

  add(user.role_code)
  add(user.roleCode)
  add(user.role?.code)
  add(user.role?.role_code)
  add(user.role?.name)
  add(user.role?.role_name)
  add(user.role?.displayName)

  const userRoles =
    (Array.isArray(user.roles) && user.roles) ||
    (Array.isArray(user.user_roles) && user.user_roles) ||
    []

  for (const role of userRoles as Array<any>) {
    if (!role) {
      continue
    }
    add(role.role_code ?? role.code)
    add(role.role_name ?? role.name)
    add(role.displayName)
  }

  return Array.from(normalized)
}

export const coerceStringValue = (value: unknown): string => {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : ""
  }
  return ""
}

export const tryParseJsonValue = <T = unknown>(value: unknown): T | null => {
  const text = coerceStringValue(value)
  if (!text) {
    return null
  }

  try {
    return JSON.parse(text) as T
  } catch {
    return null
  }
}

export const formatDateTimeDisplay = (value?: unknown): string | null => {
  const str = coerceStringValue(value)
  if (!str) return null
  const parsed = new Date(str)
  if (Number.isNaN(parsed.getTime())) {
    return str
  }
  return parsed.toLocaleString("th-TH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export const normalizeGuidValue = (value?: unknown): string | null => {
  if (typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  return GUID_PATTERN.test(trimmed) ? trimmed.toLowerCase() : null
}

export const formatDateFromIso = (isoString?: string | null) => {
  if (!isoString) return ""
  const parsed = new Date(isoString)
  if (Number.isNaN(parsed.getTime())) return ""
  return parsed.toISOString().slice(0, 10)
}

export const formatTimeFromIso = (isoString?: string | null): string => {
  if (!isoString) return ""
  const parsed = new Date(isoString)
  if (Number.isNaN(parsed.getTime())) return ""
  const hours = parsed.getHours()
  const minutes = parsed.getMinutes()
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
}

export const parseTimeValue = (value: string | null | undefined): string => {
  const raw = (value ?? "").trim()
  if (!raw) {
    return ""
  }

  const ampmMatch = raw.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i)
  if (!ampmMatch) {
    return raw
  }

  let hour = Number.parseInt(ampmMatch[1] ?? "0", 10)
  const minute = Number.parseInt(ampmMatch[2] ?? "0", 10)
  const period = ampmMatch[3]?.toUpperCase() ?? null

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return raw
  }

  if (period === "PM" && hour < 12) {
    hour += 12
  } else if (period === "AM" && hour === 12) {
    hour = 0
  }

  if (!period && hour >= 0 && hour <= 23) {
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
  }

  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
}

export const convertTimeTo24Hour = (time: string, period?: string): string => {
  if (!time) return ""
  const trimmed = time.trim()
  if (!trimmed) return ""
  const combined = period?.trim() ? `${trimmed} ${period.trim()}` : trimmed
  const normalized = parseTimeValue(combined)
  return normalized || trimmed
}

export const parseLegacyTimeValue = (
  value: string | null | undefined,
): { time: string; period: "AM" | "PM" } => {
  const normalized = parseTimeValue(value)
  if (!normalized) {
    return { time: "", period: "AM" }
  }

  const [hourPart = "0"] = normalized.split(":")
  const hour = Number.parseInt(hourPart, 10)
  if (Number.isNaN(hour)) {
    return { time: normalized, period: "AM" }
  }

  const period = hour >= 12 ? "PM" : "AM"
  return {
    time: normalized,
    period,
  }
}

const normalizeStringCandidate = (value: unknown): string => {
  return typeof value === "string" ? value.trim() : ""
}

const looksSuspiciousDisplayName = (value: string): boolean => {
  if (!value) {
    return false
  }

  if (value.includes("\uFFFD")) {
    return true
  }

  if (/[[\]{}\\|;]/.test(value)) {
    return true
  }

  const unusualCharacterCount = (value.match(/[^A-Za-z0-9\u0E00-\u0E7F\s.'-]/g) ?? []).length
  return unusualCharacterCount >= 2
}

export const resolveUserDisplayName = (user?: Record<string, any> | null): string => {
  const primaryCandidates = [
    user?.fullName,
    user?.full_name,
    user?.name,
    user?.displayName,
    user?.employeeName,
  ]

  for (const candidate of primaryCandidates) {
    const normalized = normalizeStringCandidate(candidate)
    if (normalized && !looksSuspiciousDisplayName(normalized)) {
      return normalized
    }
  }

  const fallbackCandidates = [user?.username, user?.userName, user?.email]
  for (const candidate of fallbackCandidates) {
    const normalized = normalizeStringCandidate(candidate)
    if (normalized) {
      return normalized
    }
  }

  for (const candidate of primaryCandidates) {
    const normalized = normalizeStringCandidate(candidate)
    if (normalized) {
      return normalized
    }
  }

  return ""
}

export const resolveUserCompanyName = (user?: Record<string, any> | null): string => {
  return (
    user?.company?.companyName ??
    user?.company?.name ??
    user?.companyName ??
    user?.company_name ??
    user?.companyD ??
    user?.company ??
    user?.organizationName ??
    user?.organization_name ??
    ""
  )
}

export const resolveUserCompanyId = (user?: Record<string, any> | null): string | null => {
  const candidates = [
    user?.companyId,
    user?.company_id,
    user?.organizationId,
    user?.organization_id,
    user?.company?.id,
  ]

  for (const candidate of candidates) {
    const normalized = normalizeGuidValue(candidate)
    if (normalized) {
      return normalized
    }
  }

  return null
}

export const resolveEffectiveEntryMode = ({
  entryMode,
  mode,
  forceReadOnly,
}: {
  entryMode?: PermitRequestLayoutState["entryMode"]
  mode: PermitPageMode
  forceReadOnly?: boolean
}): NonNullable<PermitRequestLayoutState["entryMode"]> => {
  if (entryMode) {
    return entryMode
  }
  if (mode === "create") {
    return "create"
  }
  return forceReadOnly ? "display" : "edit"
}

export const getPermitPageCopy = (
  entryMode: NonNullable<PermitRequestLayoutState["entryMode"]>,
): { title: string; description: string } => {
  switch (entryMode) {
    case "create":
      return {
        title: "สร้าง Permit ใหม่",
        description: "กรอกข้อมูลเพื่อสร้างเอกสารขออนุญาตทำงานใหม่",
      }
    case "display":
      return {
        title: "ดู Permit",
        description: "แสดงรายละเอียดใบอนุญาตในโหมดอ่านอย่างเดียว",
      }
    case "edit":
      return {
        title: "แก้ไข Permit",
        description: "ปรับปรุงข้อมูลใบอนุญาตก่อนส่งต่อหรือบันทึก",
      }
    case "approve":
      return {
        title: "อนุมัติ Permit",
        description: "ตรวจสอบและอนุมัติใบอนุญาตทำงาน",
      }
    case "open":
      return {
        title: "เปิด Permit",
        description: "เปิด Permit เพื่อเริ่มต้นการปฏิบัติงาน",
      }
    case "close":
      return {
        title: "ปิด Permit",
        description: "ปิด Permit หลังเสร็จสิ้นการปฏิบัติงาน",
      }
    default:
      return {
        title: "แบบฟอร์ม Permit",
        description: "",
      }
  }
}
